/**
 * Singleton manager for handling session timeouts across the application
 * Ensures only one timeout exists globally regardless of how many components use the hook
 */

// 30 minutes in milliseconds
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

class SessionTimeoutManager {
  private static instance: SessionTimeoutManager;
  private timeoutId: NodeJS.Timeout | null = null;
  private activeInstances = 0;
  private resetSessionUuidCallback: (() => void) | null = null;

  private constructor() {
    // Private constructor to prevent direct instantiation
  }

  /**
   * Get the singleton instance of SessionTimeoutManager
   */
  public static getInstance(): SessionTimeoutManager {
    if (SessionTimeoutManager.instance == null) {
      SessionTimeoutManager.instance = new SessionTimeoutManager();
    }
    return SessionTimeoutManager.instance;
  }

  /**
   * Register a hook instance with the manager
   * @returns A function to unregister the instance
   */
  public registerInstance(): () => void {
    this.activeInstances += 1;

    return () => {
      this.activeInstances -= 1;
      // Don't clear the timeout when instances reach zero
      // This ensures the timeout persists even when all components unmount
    };
  }

  /**
   * Set the callback function to reset the session UUID
   * @param callback Function to reset the session UUID or null to clear
   */
  public setResetSessionUuidCallback(callback: (() => void) | null): void {
    this.resetSessionUuidCallback = callback;
  }

  /**
   * Start or reset the session timeout
   */
  public startTimeout(): void {
    this.clearTimeout();

    this.timeoutId = setTimeout(() => {
      if (this.resetSessionUuidCallback != null) {
        this.resetSessionUuidCallback();
      }
    }, SESSION_TIMEOUT_MS);
  }

  /**
   * Clear the current timeout if it exists
   */
  public clearTimeout(): void {
    if (this.timeoutId != null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}

// Export the singleton instance
export const sessionTimeoutManager = SessionTimeoutManager.getInstance();
